import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "@/components/icons";
import { checkResetToken, confirmPasswordReset } from "@/lib/api";
import { HttpError } from "@/lib/http";

// Soft minimum for enabling submit; the backend enforces the authoritative,
// role-aware policy (10 for users, 18 for admins) and returns weak_password.
const SOFT_MIN = 10;

type Phase = "checking" | "valid" | "invalid" | "expired" | "used" | "done";

function reasonToPhase(reason: string): Phase {
  if (reason === "expired") return "expired";
  if (reason === "used") return "used";
  return "invalid";
}

/** Read the reset token from the URL fragment (preferred — a fragment is never
 *  sent to the server, so it stays out of access logs and Referer) or, as a
 *  fallback, the query string; then wipe it from the address bar so it isn't
 *  left in history. */
function takeTokenFromUrl(): string {
  let token = "";
  const hash = window.location.hash || "";
  const m = /(?:^#|&)token=([^&]+)/.exec(hash);
  if (m) token = decodeURIComponent(m[1]);
  if (!token) {
    const q = new URLSearchParams(window.location.search);
    token = q.get("token") || "";
  }
  if (token) {
    // Strip token from the visible URL + history entry immediately.
    window.history.replaceState(null, "", window.location.pathname);
  }
  return token;
}

export function ResetPasswordPage() {
  const nav = useNavigate();
  // Read + strip the token exactly once, before first paint.
  const tokenRef = React.useRef<string | null>(null);
  if (tokenRef.current === null) tokenRef.current = takeTokenFromUrl();
  const token = tokenRef.current;

  const [phase, setPhase] = React.useState<Phase>("checking");
  const [pw, setPw] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  // Keep any Referer from carrying the (now-stripped) URL to our own API too.
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    if (!token) {
      setPhase("invalid");
      return;
    }
    checkResetToken(token)
      .then((r) => {
        if (cancelled) return;
        setPhase(r.valid ? "valid" : reasonToPhase(r.reason));
      })
      .catch(() => {
        if (!cancelled) setPhase("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, pw);
      setPhase("done");
      toast.success("Password changed — you can sign in now");
      setTimeout(() => nav("/login", { replace: true }), 1200);
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as any;
        if (body?.error === "weak_password") {
          toast.error(body.message || "Password does not meet the requirements");
        } else if (body?.error === "invalid_token") {
          setPhase(reasonToPhase(body.reason || "invalid"));
        } else {
          toast.error(body?.message || err.message || "Reset failed");
        }
      } else {
        toast.error("Reset failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = pw.length >= SOFT_MIN && pw === confirm && !submitting;

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Reset your password</CardTitle>
            <CardDescription>
              {phase === "valid"
                ? "Choose a new password for your account."
                : "Password reset"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {phase === "checking" && (
              <div className="text-sm opacity-70">Checking your reset link…</div>
            )}

            {(phase === "invalid" || phase === "expired" || phase === "used") && (
              <div className="space-y-4">
                <div className="text-sm text-red-600 dark:text-red-400">
                  {phase === "expired"
                    ? "This reset link has expired."
                    : phase === "used"
                      ? "This reset link has already been used."
                      : "This reset link is invalid."}
                </div>
                <div className="text-xs opacity-60">
                  Ask an administrator to send a new password reset email, then use
                  the newest link.
                </div>
                <Button className="w-full" variant="outline" onClick={() => nav("/login")}>
                  Back to sign in
                </Button>
              </div>
            )}

            {phase === "done" && (
              <div className="space-y-4">
                <div className="text-sm text-green-600 dark:text-green-400">
                  Your password has been changed.
                </div>
                <Button className="w-full" onClick={() => nav("/login", { replace: true })}>
                  Sign in
                </Button>
              </div>
            )}

            {phase === "valid" && (
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label>New password</Label>
                  <div className="relative mt-2">
                    <Input
                      className="pr-10"
                      type={show ? "text" : "password"}
                      autoComplete="new-password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={show ? "Hide password" : "Show password"}
                      onClick={() => setShow((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="mt-1 text-xs opacity-60">
                    {pw.length}/{SOFT_MIN}+ characters
                  </div>
                </div>
                <div>
                  <Label>Confirm password</Label>
                  <Input
                    className="mt-2"
                    type={show ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                  {confirm.length > 0 && pw !== confirm && (
                    <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Passwords do not match
                    </div>
                  )}
                </div>
                <Button className="w-full" type="submit" disabled={!canSubmit}>
                  {submitting ? "Saving…" : "Set new password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
